import React, { useState, useCallback } from 'react';
import Papa from 'papaparse';
import { StageProps, RaceParticipant, SeriesParticipant } from '../types';
import { AIService } from '../services/aiService';

interface FieldMapping {
  rank: string | null;
  name: string | null;
  category: string | null;
  extraFields: string[];
}

interface CategoryMapping {
  csvCategory: string;
  seriesCategory: string | 'ADD_NEW' | 'DONT_IMPORT';
}

const FileUpload: React.FC<StageProps> = ({ appData, setAppData, onNext }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[] | null>(null);
  const [detectedFormat, setDetectedFormat] = useState<'zone4' | 'traditional' | 'series' | 'unknown'>('unknown');
  const [fieldMapping, setFieldMapping] = useState<FieldMapping | null>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const [parsedData, setParsedData] = useState<any[] | null>(null);
  
  // Category mapping state
  const [needsCategoryMapping, setNeedsCategoryMapping] = useState(false);
  const [categoryMappings, setCategoryMappings] = useState<CategoryMapping[]>([]);
  const [csvCategories, setCsvCategories] = useState<string[]>([]);
  const [seriesCategories, setSeriesCategories] = useState<string[]>([]);

  // Detect Zone4 format by looking for the characteristic structure
  const detectZone4Format = (rawData: string[][]): boolean => {
    if (rawData.length < 3) return false;
    
    // First row should start with empty cell, then have 'rank', 'time', 'status', 'firstname', 'lastname'
    const firstRow = rawData[0];
    if (firstRow.length < 6) return false;
    
    const expectedHeaders = ['', 'rank', 'time', 'status', 'firstname', 'lastname'];
    for (let i = 0; i < expectedHeaders.length; i++) {
      if (firstRow[i]?.toLowerCase().trim() !== expectedHeaders[i].toLowerCase()) {
        return false;
      }
    }
    
    // Second row should have a category name in first cell
    const secondRow = rawData[1];
    if (!secondRow || !secondRow[0]?.trim()) return false;
    
    return true;
  };

  // Detect series format (previously exported)
  const detectSeriesFormat = (headers: string[]): boolean => {
    const headerSet = new Set(headers.map(h => h.toLowerCase()));
    const seriesIndicators = ['seriesposition', 'participantid', 'totalpoints'];
    const hasRaceColumns = headers.some(header => /race.*_.*_(position|points|date)/i.test(header));
    
    return seriesIndicators.some(indicator => headerSet.has(indicator)) || hasRaceColumns;
  };

  // Parse Zone4 format into standardized structure
  const parseZone4Format = (rawData: string[][]): RaceParticipant[] => {
    const participants: RaceParticipant[] = [];
    let currentCategory = '';
    
    // Get the main headers from first row (after the standard ones)
    const firstRow = rawData[0];
    const extraHeaders = firstRow.slice(6); // Everything after firstname, lastname
    
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      if (!row || row.length === 0) continue;
      
      // Check if this is a category header row
      if (row[0]?.trim() && (!row[1] || !row[1].trim())) {
        currentCategory = row[0].trim();
        continue;
      }
      
      // Skip empty rows or rows without position
      if (!row[1]?.trim()) continue;
      
      // Extract participant data
      const participant: RaceParticipant = {
        id: `participant_${participants.length}`,
        name: `${row[4]?.trim() || ''} ${row[5]?.trim() || ''}`.trim(),
        category: currentCategory,
        position: parseInt(row[1]) || 999,
        time: row[2]?.trim() || '',
        status: row[3]?.trim() || '',
      };
      
      // Add extra fields (columns 6+)
      extraHeaders.forEach((header, index) => {
        if (header?.trim()) {
          const value = row[6 + index];
          if (value !== undefined && value !== null) {
            participant[header.trim()] = value;
          }
        }
      });
      
      participants.push(participant);
    }
    
    return participants;
  };

  // Use AI to detect field mapping for traditional format
  const detectTraditionalMapping = async (headers: string[], sampleData: any[]): Promise<FieldMapping> => {
    try {
      return await AIService.detectFieldMapping(headers, sampleData);
    } catch (error) {
      console.error('AI field mapping failed:', error);
      // Fallback to best-guess mapping
      return {
        rank: headers.find(h => /^(rank|position|place|pos)$/i.test(h)) || headers[0],
        name: headers.find(h => /^(name|participant|rider|driver)$/i.test(h)) || headers[1],
        category: headers.find(h => /^(category|class|division|group)$/i.test(h)) || headers[2],
        extraFields: headers.slice(3)
      };
    }
  };

  // Convert series format back to race format for re-processing
  const convertSeriesToRaceFormat = (seriesData: any[]): { participants: RaceParticipant[], existingSeries: SeriesParticipant[] } => {
    const participants: RaceParticipant[] = [];
    const existingSeries: SeriesParticipant[] = [];
    
    // Extract race columns
    const headers = Object.keys(seriesData[0] || {});
    const raceColumns = headers.filter(header => 
      /race.*_.*_(position|points|date)/i.test(header)
    );
    
    // Group race columns by race ID
    const raceIds = new Set<string>();
    raceColumns.forEach(col => {
      const match = col.match(/(.+)_(position|points|date)$/i);
      if (match) {
        raceIds.add(match[1]);
      }
    });
    
    console.log('Detected races from series data:', Array.from(raceIds));
    
    // Convert each participant
    seriesData.forEach((row, index) => {
      if (!row.name) return;
      
      // Create series participant
      const seriesParticipant: SeriesParticipant = {
        id: row.participantid || `participant_${index}`,
        name: row.name,
        category: row.category || '',
        license: row.license || '',
        totalPoints: parseInt(row.totalpoints) || 0,
        races: {},
        bestRaces: []
      };
      
      // Add race data
      Array.from(raceIds).forEach(raceId => {
        const position = row[`${raceId}_position`];
        const points = row[`${raceId}_points`];
        const date = row[`${raceId}_date`];
        
        if (position && points) {
          seriesParticipant.races[raceId] = {
            position: parseInt(position) || 999,
            points: parseInt(points) || 0,
            date: date || new Date().toISOString().split('T')[0]
          };
        }
      });
      
      existingSeries.push(seriesParticipant);
      
      // For re-processing, we'll use the most recent race as the "new" race
      if (raceIds.size > 0) {
        const latestRace = Array.from(raceIds).sort().pop()!;
        const raceData = seriesParticipant.races[latestRace];
        
        if (raceData) {
          participants.push({
            id: `new_${index}`,
            name: row.name,
            category: row.category || '',
            license: row.license || '',
            position: raceData.position,
            points: raceData.points,
            // Include other fields from the row
            ...Object.keys(row).reduce((acc, key) => {
              if (!['name', 'category', 'license', 'position', 'points', 'participantid', 'seriesposition', 'totalpoints'].includes(key.toLowerCase()) &&
                  !key.includes('_position') && !key.includes('_points') && !key.includes('_date')) {
                acc[key] = row[key];
              }
              return acc;
            }, {} as Record<string, any>)
          });
        }
      }
    });
    
    // Remove the latest race from existing series since it will be re-processed
    if (raceIds.size > 0) {
      const latestRace = Array.from(raceIds).sort().pop()!;
      existingSeries.forEach(participant => {
        delete participant.races[latestRace];
        // Recalculate total points without the latest race
        participant.totalPoints = Object.values(participant.races)
          .reduce((sum, race) => sum + race.points, 0);
      });
    }
    
    return { participants, existingSeries };
  };

  // Apply field mapping to traditional format data
  const applyFieldMapping = (data: any[], mapping: FieldMapping): RaceParticipant[] => {
    return data.map((row, index) => {
      const participant: RaceParticipant = {
        id: `participant_${index}`,
        name: row[mapping.name!] || '',
        category: row[mapping.category!] || '',
        position: parseInt(row[mapping.rank!]) || 999,
      };

      // Add extra fields
      mapping.extraFields.forEach(field => {
        if (row[field] !== undefined && row[field] !== null) {
          participant[field] = row[field];
        }
      });

      return participant;
    });
  };

  // Extract unique categories from participants
  const extractCategories = (participants: RaceParticipant[]): string[] => {
    const categories = new Set<string>();
    participants.forEach(p => {
      if (p.category && p.category.trim()) {
        categories.add(p.category.trim());
      }
    });
    return Array.from(categories).sort();
  };

  // Compare CSV categories with existing series categories
  const checkCategoryMapping = useCallback((csvParticipants: RaceParticipant[]) => {
    if (!appData.existingSeriesData || appData.workflowType !== 'subsequent-race') {
      return; // No category mapping needed for first race
    }

    const csvCats = extractCategories(csvParticipants);
    const seriesCats = extractCategories(appData.existingSeriesData);
    
    setCsvCategories(csvCats);
    setSeriesCategories(seriesCats);

    // Check if all categories match
    const allMatch = csvCats.every(csvCat => 
      seriesCats.some(seriesCat => 
        csvCat.toLowerCase() === seriesCat.toLowerCase()
      )
    );

    if (allMatch) {
      setNeedsCategoryMapping(false);
      return;
    }

    // Create initial mappings
    const mappings: CategoryMapping[] = csvCats.map(csvCat => {
      // Try to find exact match (case insensitive)
      const exactMatch = seriesCats.find(seriesCat => 
        csvCat.toLowerCase() === seriesCat.toLowerCase()
      );
      
      if (exactMatch) {
        return {
          csvCategory: csvCat,
          seriesCategory: exactMatch
        };
      }

      // Default to ADD_NEW for unmatched categories
      return {
        csvCategory: csvCat,
        seriesCategory: 'ADD_NEW'
      };
    });

    setCategoryMappings(mappings);
    setNeedsCategoryMapping(true);
  }, [appData.existingSeriesData, appData.workflowType]);

  // Apply category mappings to participants
  const applyCategoryMappings = (participants: RaceParticipant[]): RaceParticipant[] => {
    if (!needsCategoryMapping) {
      return participants;
    }

    const mappingLookup = new Map<string, string>();
    categoryMappings.forEach(mapping => {
      mappingLookup.set(mapping.csvCategory, mapping.seriesCategory);
    });

    return participants
      .filter(participant => {
        const mapping = mappingLookup.get(participant.category || '');
        return mapping !== 'DONT_IMPORT';
      })
      .map(participant => ({
        ...participant,
        category: (() => {
          const mapping = mappingLookup.get(participant.category || '');
          if (mapping === 'ADD_NEW') {
            return participant.category; // Keep original category name
          }
          return mapping || participant.category; // Use mapped category or original
        })()
      }));
  };

  const processFile = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);
    setNeedsConfirmation(false);
    
    try {
      const text = await file.text();
      
      // First, parse without headers to detect Zone4 format
      const rawResults = Papa.parse(text, {
        header: false,
        skipEmptyLines: true,
      });

      if (rawResults.errors.length > 0) {
        throw new Error(`CSV parsing errors: ${rawResults.errors.map(e => e.message).join(', ')}`);
      }

      const rawData = rawResults.data as string[][];
      console.log('Raw CSV data (first 5 rows):', rawData.slice(0, 5));

      // Detect format
      let format: 'zone4' | 'traditional' | 'series' | 'unknown' = 'unknown';
      
      if (detectZone4Format(rawData)) {
        format = 'zone4';
        console.log('Detected Zone4 format');
        
        const participants = parseZone4Format(rawData);
        console.log('Parsed Zone4 participants:', participants.length);
        
        // Check for category mapping needs
        checkCategoryMapping(participants);
        
        setAppData({
          ...appData,
          newRaceData: {
            participants,
            headers: Object.keys(participants[0] || {}),
            fileName: file.name
          }
        });
        
        setPreviewData(participants.slice(0, 10));
        setDetectedFormat(format);
        
      } else {
        // Parse with headers for traditional/series format
        const headerResults = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim()
        });

        const data = headerResults.data as any[];
        const headers = Object.keys(data[0] || {});
        
        console.log('CSV Headers:', headers);
        console.log('Sample data:', data.slice(0, 3));

        if (detectSeriesFormat(headers)) {
          format = 'series';
          console.log('Detected series format');
          
          const { participants, existingSeries } = convertSeriesToRaceFormat(data);
          
          // Check for category mapping needs
          checkCategoryMapping(participants);
          
          setAppData({
            ...appData,
            newRaceData: {
              participants,
              headers: Object.keys(participants[0] || {}),
              fileName: file.name,
              raceName: `Re-imported from ${file.name}`,
              raceDate: new Date().toISOString().split('T')[0]
            },
            existingSeriesData: existingSeries
          });
          
          setPreviewData(participants.slice(0, 10));
          setDetectedFormat(format);
          
        } else {
          format = 'traditional';
          console.log('Detected traditional format, requesting AI field mapping...');
          
          // Use AI to detect field mapping
          const mapping = await detectTraditionalMapping(headers, data);
          console.log('AI suggested mapping:', mapping);
          
          setFieldMapping(mapping);
          setAvailableFields(headers);
          setParsedData(data);
          setPreviewData(data.slice(0, 10));
          setDetectedFormat(format);
          setNeedsConfirmation(true);
        }
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setLoading(false);
    }
  }, [appData, setAppData, checkCategoryMapping]);

  // Confirm field mapping for traditional format
  const confirmMapping = () => {
    if (!fieldMapping || !parsedData) return;
    
    try {
      const participants = applyFieldMapping(parsedData, fieldMapping);
      console.log('Applied field mapping, participants:', participants.length);
      
      // Check for category mapping needs
      checkCategoryMapping(participants);
      
      setAppData({
        ...appData,
        newRaceData: {
          participants,
          headers: Object.keys(participants[0] || {}),
          fileName: uploadedFile?.name || 'uploaded.csv'
        }
      });
      
      setNeedsConfirmation(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply field mapping');
    }
  };

  // Update category mapping selection
  const updateCategoryMapping = (csvCategory: string, newMapping: string) => {
    setCategoryMappings(prev => 
      prev.map(mapping => 
        mapping.csvCategory === csvCategory 
          ? { ...mapping, seriesCategory: newMapping }
          : mapping
      )
    );
  };

  // Confirm category mappings and proceed
  const confirmCategoryMapping = () => {
    if (!appData.newRaceData) return;

    try {
      const mappedParticipants = applyCategoryMappings(appData.newRaceData.participants);
      console.log('Applied category mappings, participants:', mappedParticipants.length);

      setAppData({
        ...appData,
        newRaceData: {
          ...appData.newRaceData,
          participants: mappedParticipants
        }
      });

      setNeedsCategoryMapping(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply category mappings');
    }
  };

  // Update field mapping
  const updateFieldMapping = (field: keyof FieldMapping, value: string | null) => {
    if (!fieldMapping) return;
    
    setFieldMapping({
      ...fieldMapping,
      [field]: value
    });
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setUploadedFile(file);
      processFile(file);
    }
  }, [processFile]);

  const handleNext = () => {
    if (appData.newRaceData && appData.newRaceData.participants.length > 0) {
      onNext?.();
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadedFile(file);
      processFile(file);
    }
  };

  const canProceed = appData.newRaceData !== null && !needsConfirmation && !needsCategoryMapping;

  return (
    <div className="stage-container">
      <div className="stage-header">
        <h2>📂 Upload Race Data</h2>
        <p>
          Upload a CSV file containing race results. Supports Zone4 format, traditional CSV format, or previously exported series data.
        </p>
      </div>

      {error && (
        <div className="error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Enhanced File Upload Area */}
      <div style={{ marginBottom: '2rem' }}>
        <div 
          className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
          style={{
            border: `2px dashed ${dragActive ? '#2780E3' : '#cbd5e0'}`,
            borderRadius: '12px',
            padding: '3rem 2rem',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragActive ? 'rgba(39, 128, 227, 0.1)' : 'rgba(255, 255, 255, 0.05)',
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {loading ? '⏳' : dragActive ? '📤' : '📂'}
          </div>
          <h3 style={{ margin: '0 0 0.5rem 0' }}>
            {loading ? 'Processing...' : dragActive ? 'Drop CSV file here' : 'Choose or drag CSV file'}
          </h3>
          <p style={{ margin: 0, color: '#718096' }}>
            Supports Zone4, traditional, or exported series data formats
          </p>
          <input
            id="file-input"
            type="file"
            accept=".csv"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
        </div>

        {/* Format Detection and Preview */}
        {uploadedFile && (
          <div style={{ marginTop: '2rem' }}>
            <div className="file-info">
              <h4>📄 File Information</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <strong>Filename:</strong><br />
                  {uploadedFile.name}
                </div>
                <div>
                  <strong>File Size:</strong><br />
                  {(uploadedFile.size / 1024).toFixed(1)} KB
                </div>
                <div>
                  <strong>Detected Format:</strong><br />
                  <span style={{ 
                    color: detectedFormat === 'unknown' ? '#e53e3e' : '#38a169',
                    fontWeight: 'bold',
                    textTransform: 'capitalize'
                  }}>
                    {detectedFormat === 'zone4' ? 'Zone4 Racing Format' : 
                     detectedFormat === 'traditional' ? 'Traditional CSV Format' :
                     detectedFormat === 'series' ? 'Exported Series Data' : 'Unknown Format'}
                  </span>
                </div>
              </div>
            </div>

            {/* Field Mapping Confirmation for Traditional Format */}
            {needsConfirmation && fieldMapping && (
              <div style={{ marginTop: '2rem' }}>
                <div className="info-box" style={{ background: 'rgba(255, 193, 7, 0.1)', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                  <h4>🔍 Field Mapping Confirmation</h4>
                  <p>
                    Please confirm that the following field mapping is correct. You can adjust the selections if needed:
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', margin: '1rem 0' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        🏆 Position/Rank Field:
                      </label>
                      <select 
                        value={fieldMapping.rank || ''} 
                        onChange={(e) => updateFieldMapping('rank', e.target.value || null)}
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem', 
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px'
                        }}
                      >
                        <option value="">-- Select Field --</option>
                        {availableFields.map(field => (
                          <option key={field} value={field}>{field}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        👤 Name Field:
                      </label>
                      <select 
                        value={fieldMapping.name || ''} 
                        onChange={(e) => updateFieldMapping('name', e.target.value || null)}
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem', 
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px'
                        }}
                      >
                        <option value="">-- Select Field --</option>
                        {availableFields.map(field => (
                          <option key={field} value={field}>{field}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        🏷️ Category Field:
                      </label>
                      <select 
                        value={fieldMapping.category || ''} 
                        onChange={(e) => updateFieldMapping('category', e.target.value || null)}
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem', 
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px'
                        }}
                      >
                        <option value="">-- Select Field --</option>
                        {availableFields.map(field => (
                          <option key={field} value={field}>{field}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '1rem' }}>
                    <strong>Extra Fields (will be preserved):</strong><br />
                    <small style={{ color: '#718096' }}>
                      {fieldMapping.extraFields.join(', ') || 'None'}
                    </small>
                  </div>
                  
                  <div style={{ marginTop: '1.5rem' }}>
                    <button 
                      className="btn btn-primary" 
                      onClick={confirmMapping}
                      disabled={!fieldMapping.rank || !fieldMapping.name || !fieldMapping.category}
                      style={{ marginRight: '1rem' }}
                    >
                      ✅ Confirm Field Mapping
                    </button>
                    <small style={{ color: '#718096' }}>
                      All three key fields must be selected to proceed
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Format-Specific Information */}
            {detectedFormat === 'zone4' && (
              <div style={{ 
                background: 'rgba(34, 197, 94, 0.1)', 
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: '1rem'
              }}>
                <h4>🏁 Zone4 Format Detected</h4>
                <p>
                  This appears to be a Zone4 racing format CSV with multi-row headers and category sections. 
                  The data has been automatically parsed with categories and participant information extracted.
                </p>
              </div>
            )}

            {/* Series Format Information */}
            {detectedFormat === 'series' && (
              <div style={{ 
                background: 'rgba(34, 84, 61, 0.1)', 
                border: '1px solid rgba(34, 84, 61, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: '1rem'
              }}>
                <h4>📊 Series Data Detected</h4>
                <p>
                  This appears to be previously exported series data. The most recent race will be extracted 
                  and can be re-processed while preserving your existing series standings.
                </p>
              </div>
            )}

            {/* Traditional Format Information */}
            {detectedFormat === 'traditional' && !needsConfirmation && (
              <div style={{ 
                background: 'rgba(59, 130, 246, 0.1)', 
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: '1rem'
              }}>
                <h4>📋 Traditional CSV Format</h4>
                <p>
                  This appears to be a traditional CSV format. Field mapping has been applied to identify 
                  the key fields (position, name, category) while preserving all other data.
                </p>
              </div>
            )}

            {/* Data Preview */}
            {previewData && previewData.length > 0 && !needsConfirmation && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4>👀 Data Preview (First {Math.min(5, previewData.length)} rows)</h4>
                <div style={{ 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  overflowX: 'auto'
                }}>
                  <table style={{ width: '100%', fontSize: '0.9rem' }}>
                    <thead>
                      <tr>
                        {Object.keys(previewData[0]).slice(0, 6).map(header => (
                          <th key={header} style={{ 
                            padding: '0.5rem', 
                            textAlign: 'left', 
                            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#4a5568',
                            fontWeight: '600'
                          }}>
                            {header}
                          </th>
                        ))}
                        {Object.keys(previewData[0]).length > 6 && (
                          <th style={{ 
                            padding: '0.5rem', 
                            color: '#718096',
                            fontSize: '0.8rem'
                          }}>
                            ... and {Object.keys(previewData[0]).length - 6} more columns
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 5).map((row, index) => (
                        <tr key={index}>
                          {Object.entries(row).slice(0, 6).map(([key, value]) => (
                            <td key={key} style={{ 
                              padding: '0.5rem', 
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                              color: '#2d3748'
                            }}>
                              {String(value).length > 20 ? `${String(value).substring(0, 20)}...` : String(value)}
                            </td>
                          ))}
                          {Object.keys(row).length > 6 && (
                            <td style={{ 
                              padding: '0.5rem', 
                              color: '#718096',
                              fontSize: '0.8rem'
                            }}>
                              ...
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Category Mapping Interface */}
            {needsCategoryMapping && !needsConfirmation && (
              <div style={{ marginTop: '2rem' }}>
                <div className="info-box" style={{ background: 'rgba(255, 193, 7, 0.1)', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                  <h4>🏷️ Category Mapping Required</h4>
                  <p>
                    Some categories in your CSV file don't match the existing series categories. 
                    Please specify how to handle each category:
                  </p>
                  
                  <div style={{ 
                    marginTop: '1.5rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {/* Header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      backgroundColor: '#f7fafc',
                      borderBottom: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      color: '#4a5568'
                    }}>
                      <div>CSV File Categories</div>
                      <div>Series Categories</div>
                    </div>
                    
                    {/* Category Mappings */}
                    {categoryMappings.map((mapping, index) => (
                      <div 
                        key={mapping.csvCategory}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          padding: '0.75rem',
                          borderBottom: index < categoryMappings.length - 1 ? '1px solid #f7fafc' : 'none',
                          backgroundColor: 'white'
                        }}
                      >
                        {/* CSV Category */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          fontWeight: '600',
                          color: '#2d3748'
                        }}>
                          🏷️ {mapping.csvCategory}
                        </div>
                        
                        {/* Series Category Dropdown */}
                        <div>
                          <select
                            value={mapping.seriesCategory}
                            onChange={(e) => updateCategoryMapping(mapping.csvCategory, e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #e2e8f0',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              backgroundColor: mapping.seriesCategory === 'ADD_NEW' ? '#f0fff4' : 
                                            mapping.seriesCategory === 'DONT_IMPORT' ? '#fef5e7' : 'white'
                            }}
                          >
                            {/* Existing series categories */}
                            {seriesCategories.map(seriesCat => (
                              <option key={seriesCat} value={seriesCat}>
                                Match with "{seriesCat}"
                              </option>
                            ))}
                            
                            {/* Special options */}
                            <option value="ADD_NEW">Add New Category to Series</option>
                            <option value="DONT_IMPORT">Don't Import Category</option>
                          </select>
                          
                          {/* Description based on selection */}
                          <small style={{ 
                            display: 'block', 
                            marginTop: '0.25rem',
                            color: mapping.seriesCategory === 'ADD_NEW' ? '#38a169' : 
                                  mapping.seriesCategory === 'DONT_IMPORT' ? '#d69e2e' : '#718096'
                          }}>
                            {mapping.seriesCategory === 'ADD_NEW' && '✨ Will add as new category'}
                            {mapping.seriesCategory === 'DONT_IMPORT' && '⚠️ Participants will be excluded'}
                            {!['ADD_NEW', 'DONT_IMPORT'].includes(mapping.seriesCategory) && '🔗 Will rename and match'}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div style={{ marginTop: '1.5rem' }}>
                    <button 
                      className="btn btn-primary" 
                      onClick={confirmCategoryMapping}
                      style={{ marginRight: '1rem' }}
                    >
                      ✅ Confirm Category Mapping
                    </button>
                    <small style={{ color: '#718096' }}>
                      Review the mapping above and click confirm to proceed
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Category Match Success Message */}
            {appData.workflowType === 'subsequent-race' && !needsCategoryMapping && !needsConfirmation && csvCategories.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ 
                  background: 'rgba(34, 197, 94, 0.1)', 
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem'
                }}>
                  <h4>✅ All Categories Matched</h4>
                  <p>
                    All {csvCategories.length} categories in your CSV file match existing series categories. 
                    You can proceed to the next step.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="info-box">
        <h4>💡 Supported CSV Formats</h4>
        <ul>
          <li><strong>Zone4 Format:</strong> Multi-row headers with category sections (automatically detected)</li>
          <li><strong>Traditional Format:</strong> Standard CSV with position, name, and category columns (AI-assisted mapping)</li>
          <li><strong>Series Data:</strong> Previously exported series data for re-import and continuation</li>
        </ul>
        <p>The system will automatically detect your format and guide you through the appropriate parsing process.</p>
      </div>

      <div className="stage-actions">
        <button 
          className="btn btn-primary" 
          onClick={handleNext}
          disabled={!canProceed || loading}
        >
          {detectedFormat === 'series' ? 'Continue with Re-import →' : 'Next: Configure Points →'}
        </button>
      </div>
    </div>
  );
};

export default FileUpload; 